import React, { useState, Fragment, useEffect } from 'react';

import { VTStructureAnomalyData, VTCoverData, PDFImageProps } from '../../../../utils/models';

import Button from '../../../themed/button';
import Conditional from '../../../common/conditional';
import Elevation from '../../../themed/instruction-modals/elevation';
import Modal from '../../../themed/modal';
import RiskRating from '../riskRating';
import VTStructureReport from '.';
import VTCoverForm from '../../../forms/vt/vtCoverForm';
import VTStructureSummary from './vtStructureSummary';
import VTPipingReportSummary from '../../../forms/vt/vtPipingReportSummary';
import VTPhotoRecordForm from '../../../forms/vt/vtPhotoRecordForm';
import Zippy from '../../../themed/zippy';

import { getTutorials } from '../../../../store/tutorials';
import { tutorialConstants } from '../../../../utils/constants';
import { useAppSelector } from '../../../../store/configureStore';

import useAutoSaveReport from '../../../../hooks/useAutoSave';
import useProjectDetails from '../../../../hooks/useProjectDetails';

import config from '../../../../utils/config';

const CreateVTStructureReport: React.FC = () => {
	const [previewReport, setPreviewReport] = useState(false);
	const [vtStructureSummary, setVTStructureSummary] = useState('');
	const [vtCoverData, setVTCoverData] = useState<VTCoverData>();
	const [vtPhoto, setVTPhoto] = useState<PDFImageProps[]>([]);
	const [vtStructureData, setVTStructureData] = useState<VTStructureAnomalyData[]>([]);

	const togglePreviewReport = (): void => {
		setPreviewReport((p: boolean) => !p);
	};

	const getReportDetails = () => {
		return {
			anomalyData: vtStructureData,
			vtStructureReportSummary: vtStructureSummary,
			vtSummaryData: vtStructureData,
			projectDetails,
			photoRecord: vtPhoto,
			coverSheetData: vtCoverData,
		};
	};

	const saveAndPreviewReport = (): void => {
		autoSave.saveReport();
		togglePreviewReport();
	};

	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);

	const reportDetails = getReportDetails();
	const autoSave = useAutoSaveReport(reportDetails);

	useEffect(() => {
		type ReportData = ReturnType<typeof getReportDetails>;
		const cachedReport = autoSave.oldReportData as ReportData | null;

		if (cachedReport) {
			setVTCoverData(cachedReport.coverSheetData);
			setVTStructureData(cachedReport.vtSummaryData);
			setVTStructureSummary(cachedReport.vtStructureReportSummary);
			setVTPhoto(cachedReport.photoRecord);
		}
	}, [autoSave.oldReportData]);

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<VTStructureReport {...reportDetails} />
			</Modal>

			<Zippy headerTitle="Cover Sheet" isValid={!!vtCoverData}>
				<VTCoverForm onProceed={setVTCoverData} />
			</Zippy>

			<Zippy
				headerTitle="Report Summary"
				isValid={vtStructureSummary.length > 0}
			>
				<VTPipingReportSummary onProceed={setVTStructureSummary} />
			</Zippy>

			<Zippy headerTitle="Anomaly Sheet" isValid={vtStructureData.length > 0}>
				<VTStructureSummary
					anomalyData={vtStructureData}
					onDataChange={setVTStructureData}
				/>
			</Zippy>

			<Zippy headerTitle="Photographic Record" isValid={vtPhoto.length > 0}>
				<VTPhotoRecordForm onProceed={setVTPhoto} />
			</Zippy>

			<Conditional isVisible={config.enableRiskRating}>
				<Zippy headerTitle="Risk Rating" isValid={vtStructureData.length > 0}>
					<RiskRating data={vtStructureData} />
				</Zippy>
			</Conditional>

			<div className="d-flex justify-content-end">
				<Elevation
					isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}
				>
					<Button
						label="Save and Preview Report"
						disabled={error}
						onClick={saveAndPreviewReport}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

export default CreateVTStructureReport;
